import { action, computed, observable, makeObservable } from 'mobx';
import { RuleCode } from 'typings/enums';
import { IWorkflowRule } from 'typings/workflowRule';
import { IConfigurationDataManager, IEmployeeDataManager } from 'typings/dataManagers';
import { dataManagerFactory } from 'utils/MobxDataManager/singletons';
import { IBaseEmployeeLevel } from 'typings/baseEmployeeLevel';
import { IWorkflowConfiguration } from 'typings/workflowConfiguration';

export class WorkflowConfiguration implements IWorkflowConfiguration {
    private _masterAccountId: string;
    private _cycleId: number;
    @observable private _rules: IWorkflowRule[];
    @observable private _originalRules: IWorkflowRule[];
    @observable private _hasDepartment: boolean;
    @observable private _hasDivision: boolean;
    @observable private _hasBusinessUnit: boolean;
    private _configurationDataManager: IConfigurationDataManager;
    private _employeeDataManager: IEmployeeDataManager;

    constructor(workflowConfig: IWorkflowConfiguration, masterAccountId: string, cycleId: number) {
        makeObservable(this);
        this._rules = workflowConfig.rules;
        this._originalRules = workflowConfig.rules ? Array.from(workflowConfig.rules) : [];
        this._hasDepartment = workflowConfig.hasDepartment != null ? workflowConfig.hasDepartment : true;
        this._hasDivision = workflowConfig.hasDivision != null ? workflowConfig.hasDivision : true;
        this._hasBusinessUnit = workflowConfig.hasBusinessUnit != null ? workflowConfig.hasBusinessUnit : true;
        this._masterAccountId = masterAccountId;
        this._cycleId = cycleId;

        this._configurationDataManager = dataManagerFactory.getConfigurationDataManager();
        this._employeeDataManager = dataManagerFactory.getEmployeeDataManager();
    }

    get rules(): IWorkflowRule[] {
        return this._rules;
    }

    get hasDepartment(): boolean {
        return this._hasDepartment;
    }

    get hasDivision(): boolean {
        return this._hasDivision;
    }

    get hasBusinessUnit(): boolean {
        return this._hasBusinessUnit;
    }

    @computed
    get hasChanges(): boolean {
        if (this._originalRules.length !== this._rules.length) {
            return true;
        }

        return this._originalRules.some((rule, index) => {
            const newRule = this._rules[index];
            return rule.code !== newRule.code || rule.field !== newRule.field;
        });
    }

    @computed
    get hasHrApproverRule(): boolean {
        return this.hasRule({ code: RuleCode.HumanResourceApprover });
    }

    hasRule(targetRule: IWorkflowRule): boolean {
        return !!this._rules.find(rule => rule.code === targetRule.code && rule.field === targetRule.field);
    }

    @action
    addRule(rule: IWorkflowRule): void {
        this._rules.push(rule);
    }

    @action
    deleteRule(targetRule: IWorkflowRule): void {
        const index = this._rules.findIndex(rule => rule.code === targetRule.code && rule.field === targetRule.field);
        if (index >= 0) {
            this._rules.splice(index, 1);
        }
    }

    @action
    saveRules(): void {
        this._originalRules = Array.from(this._rules);
    }

    @action
    revertRules(): void {
        this._rules = Array.from(this._originalRules);
    }

    exportFullWorkflowToCsv(): Promise<void> {
        return this._configurationDataManager.exportFullWorkflowToCsv(this._masterAccountId, this._cycleId);
    }

    @action
    async setDivisionDepartmentBusinessUnitAvailability(): Promise<void> {
        const [
            employeeLevelDepartments,
            employeeLevelBusinessUnits,
            employeeLevelDivisions
        ]: Nullable<IBaseEmployeeLevel[] | string[]>[] = await Promise.all([
            this._employeeDataManager.getEmployeeDepartments(this._masterAccountId),
            this._employeeDataManager.getEmployeeBusinessUnits(this._masterAccountId),
            this._employeeDataManager.getEmployeeDivisions(this._masterAccountId)
        ]);

        this._hasDepartment = !!employeeLevelDepartments?.length;
        this._hasDivision = !!employeeLevelDivisions?.length;
        this._hasBusinessUnit = !!employeeLevelBusinessUnits?.length;
    }

    async getWorkflowDetails(): Promise<Nullable<string[][]>> {
        return this._configurationDataManager.getWorkflowDetails(this._masterAccountId, this._cycleId);
    }
}